"use client";

import { useCallback } from "react";
import type { JobItemDto } from "@artha/api-contracts";
import type { JobFiltersState } from "@artha/ui";
import { ANALYTICS_EVENTS } from "./events";
import { useAnalytics } from "./useAnalytics";
import { useLocalStorage } from "@/lib/useLocalStorage";
import { useAudienceAnalyticsContext } from "./AudienceAnalyticsContext";
import {
  buildAudienceJobFilterProps,
  buildAudienceJobProps,
  buildAudienceJobSearchProps,
  buildAudienceSocialMediaClickProps,
  buildAudienceEarnClickProps,
  buildAudienceVpnRestrictionProps,
  buildAudienceCountryRestrictionProps,
  buildAudienceIncognitoRestrictionProps,
  getChangedFilterKeys,
  getFiltersAppliedCount,
} from "./helpers";

/**
 * Hook for tracking audience analytics events
 * Automatically includes the creator's username from context in all events
 * 
 * @param userNameOverride - Optional username to override context value
 */
export function useAudienceAnalytics(userNameOverride?: string) {
  const { trackEvent } = useAnalytics();
  const { username: contextUsername } = useAudienceAnalyticsContext();
  
  // Use override if provided, otherwise fall back to context
  const rawUsername = userNameOverride ?? contextUsername;
  const username =
    rawUsername && rawUsername.startsWith("@") ? rawUsername.slice(1) : rawUsername;
  const storageKey = "aud_job_view_counts";
  const { read: readViewCounts, write: writeViewCounts } = useLocalStorage<
    Record<string, number>
  >(storageKey, {});


  const trackSocialMediaClick = useCallback(
    (socialMedia: string) => {
      const props = {
        ...buildAudienceSocialMediaClickProps(socialMedia),
        ...(username && { username }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_SOCIALMEDIA_CLICK, props);
    },
    [trackEvent, username]
  );

  const trackJobView = useCallback(
    (job: JobItemDto) => {
      const counts = readViewCounts();
      counts[job.id] = (counts[job.id] ?? 0) + 1;
      writeViewCounts(counts);
      const props = {
        ...buildAudienceJobProps(job),
        ...(username && { username }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_JOB_VIEW, props);
    },
    [trackEvent, username, readViewCounts, writeViewCounts]
  );

  const trackJobApply = useCallback(
    (job: JobItemDto) => {
      const counts = readViewCounts();
      const jobsViewedInSession = counts[job.id] ?? 0;
      const props = {
        ...buildAudienceJobProps(job),
        jobs_viewed_in_session: jobsViewedInSession,
        ...(username && { username }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_JOB_APPLY, props);

      if (jobsViewedInSession > 0) {
        delete counts[job.id];
        writeViewCounts(counts);
      }
    },
    [trackEvent, username, readViewCounts, writeViewCounts]
  );

  const trackJobSearch = useCallback(
    (query: string) => {
      const props = {
        ...buildAudienceJobSearchProps(query),
        ...(username && { username }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_JOB_SEARCH, props);
    },
    [trackEvent, username]
  );

  const trackJobFilterJob = useCallback(
    (previous: JobFiltersState, next: JobFiltersState) => {
      const filtersChanged = getChangedFilterKeys(previous, next);
      if (filtersChanged.length === 0) return;
      const filtersAppliedCount = getFiltersAppliedCount(next);
      const props = {
        ...buildAudienceJobFilterProps(filtersChanged, filtersAppliedCount),
        ...(username && { username }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_JOB_FILTER_JOB, props);
    },
    [trackEvent, username]
  );

  const trackAudEarnClick = useCallback(
    (username: string) => {
      const props = {
        ...buildAudienceEarnClickProps(username),
        ...(username && { username }),
      };
      trackEvent(ANALYTICS_EVENTS.AUD_EARN_CLICK, props);
    },
    [trackEvent, username]
  );

  const trackVpnRestrictionShown = useCallback(
    (ipTimezone: string, browserTimezone: string, job?: JobItemDto | null, creator?: any) => {
      const props = {
        ...buildAudienceVpnRestrictionProps(ipTimezone, browserTimezone, job, creator),
        ...(username && { username }),
      };
      console.debug("[Analytics] trackVpnRestrictionShown", props);
      trackEvent(ANALYTICS_EVENTS.AUD_VPN_RESTRICTION_SHOWN, props);
    },
    [trackEvent, username]
  );

  const trackCountryRestrictionShown = useCallback(
    (seekerCountry: string, jobCountry: string, job: JobItemDto, creator?: any) => {
      const props = {
        ...buildAudienceCountryRestrictionProps(seekerCountry, jobCountry, job, creator),
        ...(username && { username }),
      };
      console.debug("[Analytics] trackCountryRestrictionShown", props);
      trackEvent(ANALYTICS_EVENTS.AUD_COUNTRY_RESTRICTION_SHOWN, props);
    },
    [trackEvent, username]
  );

  const trackIncognitoRestrictionShown = useCallback(
    (job?: JobItemDto | null, creator?: any) => {
      const props = {
        ...buildAudienceIncognitoRestrictionProps(job, creator),
        ...(username && { username }),
      };
      console.debug("[Analytics] trackIncognitoRestrictionShown", props);
      trackEvent(ANALYTICS_EVENTS.AUD_INCOGNITO_RESTRICTION_SHOWN, props);
    },
    [trackEvent, username]
  );

  return {
    trackSocialMediaClick,
    trackJobView,
    trackJobApply,
    trackJobSearch,
    trackJobFilterJob,
    trackAudEarnClick,
    trackVpnRestrictionShown,
    trackCountryRestrictionShown,
    trackIncognitoRestrictionShown,
  };
}
